/**
 * Hard safety rails — architecture §6 step 4 (VALIDATE).
 *
 * These run after the deterministic proposal (and would run after any LLM-authored
 * proposal too) and they always win. They are deliberately conservative: it is
 * better to under-prescribe than to hand a lifter a 20% jump while they are hurt.
 *
 * Rails:
 *   LOAD_INCREASE_CAPPED      a single exercise never gains more than +10% load
 *   VOLUME_INCREASE_CAPPED    total weekly tonnage never rises more than +30%
 *   VOLUME_DECREASE_CAPPED    total weekly tonnage never falls more than -40%
 *   RPE_CLAMPED               targetRpe is kept inside [5, 9.5]
 *   INJURY_MOVEMENT_BLOCKED   flagged movements become `blocked` + a substitution
 *   DELOAD_OVERRIDES_INCREASE a deload verdict cancels any increase in the proposal
 */
import type { ActivePlan, CoachProfile } from '../types/coach.js';
import type {
  EvaluationResult,
  PlanProposal,
  PrescribedChange,
  PrescribedChangeField,
  SafetyRail,
  SafetyRailOutcome,
  Verdict,
} from '../types/results.js';
import { flattenPlanExercises, planWeeklyVolume } from './matrix.js';
import { round2, roundToIncrement } from './math.js';

export const MAX_SINGLE_EXERCISE_LOAD_INCREASE = 0.1; // +10%
export const MAX_WEEKLY_VOLUME_INCREASE = 0.3; // +30%
export const MAX_WEEKLY_VOLUME_DECREASE = 0.4; // -40% (i.e. floor at 60%)
export const MAX_TARGET_RPE = 9.5;
export const MIN_TARGET_RPE = 5;
export const MIN_LOAD_STEP_KG = 0.5;

interface InjuryMovementPattern {
  flag: string;
  /** Canonical movement family the flag forbids. */
  family: string;
  /** Matches the exercise name. */
  pattern: RegExp;
  /** Substitution, with `{name}` replaced by the blocked exercise name. */
  substitute: string;
}

/**
 * Injury-flag -> forbidden movement family. Patterns default to case-insensitive
 * and use `[\\s_-]*` so `left_shoulder`, `left shoulder` and `Left-Shoulder` all
 * normalise to the same flag.
 */
export const INJURY_MOVEMENT_MAP: ReadonlyArray<InjuryMovementPattern> = [
  {
    flag: 'left_shoulder',
    family: 'overhead-press',
    pattern: /overhead press|shoulder press|military press|push press|arnold press/i,
    substitute: 'seated neutral-grip dumbbell press (pain-free range)',
  },
  {
    flag: 'right_shoulder',
    family: 'overhead-press',
    pattern: /overhead press|shoulder press|military press|push press|arnold press/i,
    substitute: 'seated neutral-grip dumbbell press (pain-free range)',
  },
  {
    flag: 'left_shoulder',
    family: 'lateral-raise',
    pattern: /lateral raise|side raise|rear delt raise|upright row/i,
    substitute: 'cable external rotation and scapular retraction work',
  },
  {
    flag: 'right_shoulder',
    family: 'lateral-raise',
    pattern: /lateral raise|side raise|rear delt raise|upright row/i,
    substitute: 'cable external rotation and scapular retraction work',
  },
  {
    flag: 'left_shoulder',
    family: 'incline-press',
    pattern: /incline .*press/i,
    substitute: 'flat or floor press with a neutral grip',
  },
  {
    flag: 'right_shoulder',
    family: 'incline-press',
    pattern: /incline .*press/i,
    substitute: 'flat or floor press with a neutral grip',
  },
  {
    flag: 'left_shoulder',
    family: 'face-pull',
    pattern: /face pull|high pull|band pull-apart/i,
    substitute: 'prone Y-raise on an incline bench',
  },
  {
    flag: 'right_shoulder',
    family: 'face-pull',
    pattern: /face pull|high pull|band pull-apart/i,
    substitute: 'prone Y-raise on an incline bench',
  },
  {
    flag: 'lower_back',
    family: 'barbell-row',
    pattern: /barbell row|bent[- ]over row|pendlay row|t[- ]bar row/i,
    substitute: 'chest-supported dumbbell row',
  },
  {
    flag: 'lower_back',
    family: 'romanian-deadlift',
    pattern: /romanian deadlift|rdl|stiff[- ]leg deadlift|good morning/i,
    substitute: 'seated or lying leg curl',
  },
  {
    flag: 'lower_back',
    family: 'back-squat',
    pattern: /back squat|barbell squat|front squat|low[- ]bar squat|high[- ]bar squat/i,
    substitute: 'leg press or belt squat with a neutral spine',
  },
  {
    flag: 'left_knee',
    family: 'squat',
    pattern: /squat|hack squat|pistol squat/i,
    substitute: 'box squat to a comfortable depth, or hip thrust',
  },
  {
    flag: 'right_knee',
    family: 'squat',
    pattern: /squat|hack squat|pistol squat/i,
    substitute: 'box squat to a comfortable depth, or hip thrust',
  },
  {
    flag: 'left_knee',
    family: 'leg-press',
    pattern: /leg press/i,
    substitute: 'split squat limited to a pain-free range',
  },
  {
    flag: 'right_knee',
    family: 'leg-press',
    pattern: /leg press/i,
    substitute: 'split squat limited to a pain-free range',
  },
  {
    flag: 'left_knee',
    family: 'split-squat',
    pattern: /split squat|lunge|step[- ]up/i,
    substitute: 'leg extension with a controlled tempo',
  },
  {
    flag: 'right_knee',
    family: 'split-squat',
    pattern: /split squat|lunge|step[- ]up/i,
    substitute: 'leg extension with a controlled tempo',
  },
  {
    flag: 'left_knee',
    family: 'leg-curl',
    pattern: /leg curl|nordic curl|good morning/i,
    substitute: 'hip thrust or glute bridge',
  },
  {
    flag: 'right_knee',
    family: 'leg-curl',
    pattern: /leg curl|nordic curl|good morning/i,
    substitute: 'hip thrust or glute bridge',
  },
  {
    flag: 'left_elbow',
    family: 'curl',
    pattern: /curl/i,
    substitute: 'hammer curl with a neutral wrist',
  },
  {
    flag: 'right_elbow',
    family: 'curl',
    pattern: /curl/i,
    substitute: 'hammer curl with a neutral wrist',
  },
  {
    flag: 'left_elbow',
    family: 'pushdown',
    pattern: /pushdown|push[- ]down|skull ?crusher|overhead triceps? extension|dip\b/i,
    substitute: 'band triceps extension at a pain-free angle',
  },
  {
    flag: 'right_elbow',
    family: 'pushdown',
    pattern: /pushdown|push[- ]down|skull ?crusher|overhead triceps? extension|dip\b/i,
    substitute: 'band triceps extension at a pain-free angle',
  },
  {
    flag: 'left_elbow',
    family: 'close-grip-bench',
    pattern: /close[- ]grip bench|close[- ]grip press|jm press/i,
    substitute: 'dumbbell floor press',
  },
  {
    flag: 'right_elbow',
    family: 'close-grip-bench',
    pattern: /close[- ]grip bench|close[- ]grip press|jm press/i,
    substitute: 'dumbbell floor press',
  },
  {
    flag: 'left_wrist',
    family: 'curl',
    pattern: /curl/i,
    substitute: 'neutral-grip (hammer) curl with wrist straps',
  },
  {
    flag: 'right_wrist',
    family: 'curl',
    pattern: /curl/i,
    substitute: 'neutral-grip (hammer) curl with wrist straps',
  },
  {
    flag: 'left_wrist',
    family: 'overhead-press',
    pattern: /overhead press|shoulder press|military press/i,
    substitute: 'neutral-grip dumbbell press with wrists stacked',
  },
  {
    flag: 'right_wrist',
    family: 'overhead-press',
    pattern: /overhead press|shoulder press|military press/i,
    substitute: 'neutral-grip dumbbell press with wrists stacked',
  },
];

/** Normalise a stored injury flag: `left_shoulder` / `left shoulder` / `Left-Shoulder`. */
export function normaliseInjuryFlag(flag: string): string {
  return flag.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

export interface InjuryHit {
  flag: string;
  family: string;
  substitute: string;
}

/** First injury-flag conflict for a movement name, or `null` when it is clear. */
export function injuryConflictFor(
  exerciseName: string,
  injuryFlags: ReadonlyArray<string>,
): InjuryHit | null {
  const normalised = injuryFlags.map(normaliseInjuryFlag);
  for (const pattern of INJURY_MOVEMENT_MAP) {
    if (!normalised.includes(pattern.flag)) continue;
    if (pattern.pattern.test(exerciseName)) {
      return { flag: pattern.flag, family: pattern.family, substitute: pattern.substitute };
    }
  }
  return null;
}

type NumericField = 'sets' | 'repMin' | 'repMax' | 'targetWeightKg' | 'targetRpe';

function withField(
  change: PrescribedChange,
  name: NumericField,
  after: number | null,
): PrescribedChange {
  const exists = change.fields.some((entry) => entry.field === name);
  const fields: PrescribedChangeField[] = exists
    ? change.fields.map((entry) =>
        entry.field === name ? { ...entry, after } : entry,
      )
    : [...change.fields, { field: name, before: null, after, unit: unitForField(name) }];
  return { ...change, fields };
}

function unitForField(name: NumericField): string {
  switch (name) {
    case 'sets':
      return 'sets';
    case 'repMin':
    case 'repMax':
      return 'reps';
    case 'targetWeightKg':
      return 'kg';
    case 'targetRpe':
      return 'rpe';
    default:
      return '';
  }
}

interface VolumeComponents {
  beforeSets: number;
  beforeWeight: number;
  afterSets: number;
  afterWeight: number;
}

/** Sets × load, before and after a change. Missing fields are filled from the plan level. */
function componentsOf(change: PrescribedChange, fallback: VolumeComponents): VolumeComponents {
  const components: VolumeComponents = { ...fallback };
  for (const entry of change.fields) {
    if (entry.field === 'sets') {
      components.beforeSets = entry.before ?? fallback.beforeSets;
      components.afterSets = entry.after ?? fallback.afterSets;
    }
    if (entry.field === 'targetWeightKg') {
      components.beforeWeight = entry.before ?? fallback.beforeWeight;
      components.afterWeight = entry.after ?? fallback.afterWeight;
    }
  }
  return components;
}

/** Per-exercise baseline (sets × prescribed load) pulled from the active plan. */
function planFallbackByExercise(plan: ActivePlan | null): Map<string, VolumeComponents> {
  const byId = new Map<string, VolumeComponents>();
  for (const { exercise } of flattenPlanExercises(plan)) {
    const weight = Math.max(exercise.targetWeightKg ?? 0, 0);
    byId.set(exercise.planExerciseId, {
      beforeSets: exercise.sets,
      beforeWeight: weight,
      afterSets: exercise.sets,
      afterWeight: weight,
    });
  }
  return byId;
}

function recomputeVolumeDelta(change: PrescribedChange, fallback: VolumeComponents): number {
  const components = componentsOf(change, fallback);
  return round2(
    components.afterSets * components.afterWeight - components.beforeSets * components.beforeWeight,
  );
}

/** Weekly tonnage implied by a change list, given the plan it is applied to. */
export function proposalWeeklyVolume(
  plan: ActivePlan | null,
  changes: ReadonlyArray<PrescribedChange>,
): number {
  const fallbacks = planFallbackByExercise(plan);
  let volume = planWeeklyVolume(plan);
  for (const change of changes) {
    const fallback = fallbacks.get(change.planExerciseId);
    if (!fallback) continue;
    volume += recomputeVolumeDelta(change, fallback);
  }
  return round2(volume);
}

function baseVolumeOf(plan: ActivePlan | null): number {
  return planWeeklyVolume(plan);
}

function clampLoadIncrease(
  change: PrescribedChange,
  rails: SafetyRail[],
): PrescribedChange {
  const before = change.fields.find((entry) => entry.field === 'targetWeightKg')?.before ?? null;
  const after = change.fields.find((entry) => entry.field === 'targetWeightKg')?.after ?? null;
  if (before === null || after === null) return change;
  if (after <= before) return change;
  const maxAllowed = round2(before * (1 + MAX_SINGLE_EXERCISE_LOAD_INCREASE));
  if (after <= maxAllowed) return change;
  const snapped = Math.max(
    roundToIncrement(maxAllowed, MIN_LOAD_STEP_KG),
    round2(before + MIN_LOAD_STEP_KG),
  );
  rails.push({
    code: 'LOAD_INCREASE_CAPPED',
    explanation: `${change.exerciseName} was set to jump from ${before} kg to ${after} kg — that is more than +10% in one week. Capped at ${snapped} kg.`,
    exerciseName: change.exerciseName,
    detail: `before=${before}kg requested=${after}kg cap=${maxAllowed}kg applied=${snapped}kg`,
  });
  return withField(change, 'targetWeightKg', snapped);
}

function clampRpe(change: PrescribedChange, rails: SafetyRail[]): PrescribedChange {
  const entry = change.fields.find((field) => field.field === 'targetRpe');
  if (!entry || entry.after === null) return change;
  if (entry.after >= MIN_TARGET_RPE && entry.after <= MAX_TARGET_RPE) return change;
  const clamped = round2(Math.min(Math.max(entry.after, MIN_TARGET_RPE), MAX_TARGET_RPE));
  rails.push({
    code: 'RPE_CLAMPED',
    explanation: `${change.exerciseName} was prescribed at RPE ${entry.after}; the coach keeps target RPE between ${MIN_TARGET_RPE} and ${MAX_TARGET_RPE}. Set to RPE ${clamped}.`,
    exerciseName: change.exerciseName,
    detail: `requested=${entry.after} applied=${clamped} bounds=[${MIN_TARGET_RPE},${MAX_TARGET_RPE}]`,
  });
  return withField(change, 'targetRpe', clamped);
}

function blockInjuredMovement(
  change: PrescribedChange,
  hit: InjuryHit,
  rails: SafetyRail[],
): PrescribedChange {
  const increased =
    change.action === 'increase_load' ||
    (change.fields.some((entry) => {
      if (entry.field !== 'targetWeightKg' && entry.field !== 'sets') return false;
      return entry.before !== null && entry.after !== null && entry.after > entry.before;
    }));
  rails.push({
    code: 'INJURY_MOVEMENT_BLOCKED',
    explanation: `${change.exerciseName} is off the table while the ${hit.flag.replace(/_/g, ' ')} flag is active. Substitution: ${hit.substitute}.${increased ? ' No load increase was applied.' : ''}`,
    exerciseName: change.exerciseName,
    detail: `flag=${hit.flag} family=${hit.family} requestedAction=${change.action}`,
  });
  return {
    ...change,
    action: 'blocked',
    rule: `safety_rail.injury_${hit.family.replace(/-/g, '_')}`,
    rationale: `${change.exerciseName} stays blocked while the ${hit.flag.replace(/_/g, ' ')} flag is active. Swap it for ${hit.substitute} at the same sets and reps, and keep it pain-free.`,
    note: `Substitution: ${hit.substitute}`,
  };
}

/**
 * Apply every hard rail. Returns a new proposal (changes are never mutated) plus
 * the exact list of rails that fired, for the report and `coach_reports.safety_rails_hit`.
 */
export function applySafetyRails(
  proposal: PlanProposal,
  evaluation: EvaluationResult,
  profile: CoachProfile,
  activePlan: ActivePlan | null = null,
): SafetyRailOutcome {
  const rails: SafetyRail[] = [];
  const injuryFlags = profile.injuryFlags ?? [];

  let changes: PrescribedChange[] = proposal.changes.map((entry) => ({ ...entry }));

  // 1. Deload always wins over any increase.
  if (evaluation.verdict === 'deload_recommended') {
    changes = changes.map((entry) => {
      // A genuine increase is a change that raises load or sets. A deload-level
      // reduction is left alone; only real increases are cancelled.
      const isIncrease =
        entry.action === 'increase_load' ||
        entry.fields.some(
          (field) =>
            (field.field === 'targetWeightKg' || field.field === 'sets') &&
            field.before !== null &&
            field.after !== null &&
            field.after > field.before,
        );
      if (!isIncrease) return entry;
      rails.push({
        code: 'DELOAD_OVERRIDES_INCREASE',
        explanation: `${entry.exerciseName} was queued for an increase, but the block is a deload — the increase is cancelled.`,
        exerciseName: entry.exerciseName,
        detail: `verdict=${evaluation.verdict} action=${entry.action}`,
      });
      const deloaded = entry.fields.map((field) => {
        if (
          (field.field === 'targetWeightKg' || field.field === 'sets') &&
          field.before !== null &&
          field.after !== null &&
          field.after > field.before
        ) {
          return { ...field, after: field.before };
        }
        return field;
      });
      return {
        ...entry,
        action: 'deload' as const,
        rule: 'safety_rail.deload_overrides_increase',
        rationale: `${entry.exerciseName}: deload week wins — no increases this week.`,
        fields: deloaded,
      };
    });
  }

  // 2. Per-exercise load cap (+10%).
  changes = changes.map((entry) => clampLoadIncrease(entry, rails));

  // 3. RPE bounds.
  changes = changes.map((entry) => clampRpe(entry, rails));

  // 4. Injury flags: block the movement and substitute.
  changes = changes.map((entry) => {
    const hit = injuryConflictFor(entry.exerciseName, injuryFlags);
    if (!hit || entry.action === 'no_change') return entry;
    return blockInjuredMovement(entry, hit, rails);
  });

  // 5. Whole-week tonnage window [-40%, +30%].
  const baseVolume = baseVolumeOf(activePlan);
  const fallbacks = planFallbackByExercise(activePlan);
  // A deload is a deliberate reduction: the volume rails must not "correct" it.
  const deloadVerdict = evaluation.verdict === 'deload_recommended';
  if (baseVolume > 0 && !deloadVerdict) {
    const requestedVolume = proposalWeeklyVolume(activePlan, changes);

    if (requestedVolume > round2(baseVolume * (1 + MAX_WEEKLY_VOLUME_INCREASE))) {
      const target = round2(baseVolume * (1 + MAX_WEEKLY_VOLUME_INCREASE));
      const increase = requestedVolume - baseVolume;
      const scale = increase > 0 ? (target - baseVolume) / increase : 0;
      changes = changes.map((entry) => {
        const fallback = fallbacks.get(entry.planExerciseId);
        if (!fallback || recomputeVolumeDelta(entry, fallback) <= 0) return entry;
        const components = componentsOf(entry, fallback);
        // Weight deltas scale first: they are the safest lever to pull back.
        const weightDelta = round2((components.afterWeight - components.beforeWeight) * scale);
        const newWeight = round2(Math.max(components.beforeWeight + weightDelta, 0));
        let scaled = withField(entry, 'targetWeightKg', newWeight);
        if (components.afterSets > components.beforeSets) {
          const scaledComponents = componentsOf(scaled, fallback);
          const weightOnlyDelta = recomputeVolumeDelta(scaled, fallback);
          // Spend what is left of this exercise's budget on extra sets, if any.
          const headroom = Math.max(0, round2(recomputeVolumeDelta(entry, fallback) * scale) - weightOnlyDelta);
          const effectiveWeight = Math.max(scaledComponents.afterWeight, scaledComponents.beforeWeight);
          const extraSets = effectiveWeight > 0 ? Math.floor(headroom / effectiveWeight) : 0;
          const newSets = Math.min(
            components.beforeSets + Math.max(extraSets, 0),
            components.afterSets,
          );
          if (newSets < components.afterSets) scaled = withField(scaled, 'sets', newSets);
        }
        return scaled;
      });
      // Safety net: recompute and trim any residual set increase until the cap holds.
      let applied = proposalWeeklyVolume(activePlan, changes);
      let guard = 0;
      while (applied > target && guard < 200) {
        guard += 1;
        let trimmed = false;
        changes = changes.map((entry) => {
          if (trimmed || applied <= target) return entry;
          const fallback = fallbacks.get(entry.planExerciseId);
          if (!fallback) return entry;
          const components = componentsOf(entry, fallback);
          if (components.afterSets <= components.beforeSets) return entry;
          const next = withField(entry, 'sets', components.afterSets - 1);
          trimmed = true;
          return next;
        });
        const next = proposalWeeklyVolume(activePlan, changes);
        if (next === applied) break;
        applied = next;
      }
      rails.push({
        code: 'VOLUME_INCREASE_CAPPED',
        explanation: `The proposal added more than +${Math.round(MAX_WEEKLY_VOLUME_INCREASE * 100)}% weekly volume. Scaled back to ${applied.toFixed(0)} kg against a ${baseVolume.toFixed(0)} kg baseline.`,
        detail: `baseline=${baseVolume}kg requested=${requestedVolume}kg cap=${target}kg applied=${applied}kg scale=${round2(scale)}`,
      });
    } else if (requestedVolume < round2(baseVolume * (1 - MAX_WEEKLY_VOLUME_DECREASE))) {
      const target = round2(baseVolume * (1 - MAX_WEEKLY_VOLUME_DECREASE));
      const shortfall = baseVolume - requestedVolume;
      const scale = shortfall > 0 ? (baseVolume - target) / shortfall : 0;
      changes = changes.map((entry) => {
        const fallback = fallbacks.get(entry.planExerciseId);
        if (!fallback || recomputeVolumeDelta(entry, fallback) >= 0) return entry;
        const components = componentsOf(entry, fallback);
        const weightDelta = round2((components.afterWeight - components.beforeWeight) * scale);
        const newWeight = round2(Math.max(components.beforeWeight + weightDelta, 0));
        const restoredSets = components.afterSets < components.beforeSets ? components.beforeSets : components.afterSets;
        const withWeight = withField(entry, 'targetWeightKg', newWeight);
        const withSets = withField(
          withWeight,
          'sets',
          Math.min(restoredSets, Math.max(components.beforeSets, 1)),
        );
        // Keep the smaller of the two restorations, so we stay as close to the floor as possible.
        const weightOnlyDelta = recomputeVolumeDelta(withWeight, fallback);
        const bothDelta = recomputeVolumeDelta(withSets, fallback);
        if (bothDelta < 0 && Math.abs(bothDelta) >= Math.abs(target - baseVolume)) return withWeight;
        return bothDelta < weightOnlyDelta ? withWeight : withSets;
      });
      let applied = proposalWeeklyVolume(activePlan, changes);
      // Bounded fallback: if weight-only restoration is still short, restore sets step by step.
      let guard = 0;
      while (applied < target && guard < 1000) {
        guard += 1;
        let progressed = false;
        changes = changes.map((entry) => {
          if (progressed || applied >= target) return entry;
          const fallback = fallbacks.get(entry.planExerciseId);
          if (!fallback) return entry;
          const components = componentsOf(entry, fallback);
          if (components.afterSets >= components.beforeSets) return entry;
          const bumped = withField(entry, 'sets', components.afterSets + 1);
          if (recomputeVolumeDelta(bumped, fallback) > recomputeVolumeDelta(entry, fallback)) {
            progressed = true;
            return bumped;
          }
          return entry;
        });
        const next = proposalWeeklyVolume(activePlan, changes);
        if (next === applied) break;
        applied = next;
      }
      rails.push({
        code: 'VOLUME_DECREASE_CAPPED',
        explanation: `The proposal cut more than ${Math.round(MAX_WEEKLY_VOLUME_DECREASE * 100)}% of weekly volume. Floored at ${applied.toFixed(0)} kg against a ${baseVolume.toFixed(0)} kg baseline.`,
        detail: `baseline=${baseVolume}kg requested=${requestedVolume}kg floor=${target}kg applied=${applied}kg scale=${round2(scale)}`,
      });
    }
  }

  return {
    proposal: { ...proposal, changes },
    railsHit: rails,
  };
}

/** Verdicts that may only ever reduce work — used by callers that bypass `applySafetyRails`. */
export function verdictAllowsLoadIncrease(verdict: Verdict): boolean {
  return verdict !== 'deload_recommended' && verdict !== 'insufficient_data';
}
